import interactionResponse from 'await-interaction-response';
import classnames from 'classnames';
import {
  useEffect,
  useRef,
  useState,
  type FunctionComponent,
  type KeyboardEvent,
  type MouseEvent,
  type PropsWithChildren,
} from 'react';

import { ArrowDown } from '../icons/ArrowDown';
import { useAccordionContext } from './Accordion';
import { useResizeObserver } from './hooks/useResizeObserver';

import styles from './AccordionItem.module.scss';

export type AccordionItemToggleHandler = (
  event: KeyboardEvent<HTMLDivElement> | MouseEvent<HTMLDivElement>,
  id: string | number,
  opened: boolean,
) => unknown;

export interface AccordionItemProps extends PropsWithChildren<unknown> {
  /**
   * Unique id to be passed to AccordionItem
   */
  id: string | number;
  /**
   * Define the title for the AccordionItem
   */
  title: string | JSX.Element;
  /**
   * Whether to open the AccordionItem or not
   */
  open?: boolean;
  /**
   * Disable the openable feature
   */
  disabled?: boolean;
  /**
   * To override the style for AccordionItem
   */
  className?: string;
  /**
   * To override the style for the header AccordionItem
   */
  headerClassName?: string;
  /**
   * To override the style for the content AccordionItem
   */
  contentClassName?: string;
  /**
   * To override the style for the body AccordionItem
   */
  bodyClassName?: string;
  /**
   * Function to be called with (event, id, isOpen) on clicking the header sections in AccordionItem
   *
   */
  onToggle?: AccordionItemToggleHandler;
}

export const AccordionItem: FunctionComponent<AccordionItemProps> = ({
  open = false,
  disabled = false,
  className,
  headerClassName,
  contentClassName,
  bodyClassName,
  id,
  title,
  children,
  onToggle,
  ...restProps
}) => {
  const sectionId = `section-${id}`;
  const labelId = `label-${id}`;
  const { type, activeAccordion, manual, hiddenArrowIcon, onToggleAccordion } =
    useAccordionContext();

  const [isInit, setIsInit] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const isAccordionOpen = manual ? open : activeAccordion === id;
  const height = useResizeObserver(bodyRef.current) || 'auto';

  const onClickAccordion = async (
    event: KeyboardEvent<HTMLDivElement> | MouseEvent<HTMLDivElement>,
  ) => {
    event.preventDefault();
    await interactionResponse();
    if (onToggle) {
      onToggle(event, id, !isAccordionOpen);
    }

    if (onToggleAccordion) {
      onToggleAccordion(id);
    }
  };

  const onHandleToggle = (event: MouseEvent<HTMLDivElement>) => {
    if (!disabled && headerRef.current?.contains(event.target as Node)) {
      onClickAccordion(event);
    }
  };

  const onHandleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (
      !disabled &&
      headerRef.current?.contains(event.target as Node) &&
      event.key === 'Enter'
    ) {
      onClickAccordion(event);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      const isCurrentRef =
        bodyRef && bodyRef.current && bodyRef.current.getBoundingClientRect;
      if (
        !isInit &&
        isCurrentRef &&
        bodyRef.current &&
        height !== bodyRef.current.getBoundingClientRect().height
      ) {
        setIsInit(true);
      }
    }, 0);
  }, [height, isInit]);

  const rootClassnames = classnames(
    styles.root,
    {
      [styles.open]: isAccordionOpen,
      [styles.flat]: type === 'flat',
      [styles.outlined]: type === 'outlined',
      [styles.dividers]: type === 'dividers',
      [styles.disabled]: disabled,
    },
    className,
  );

  const headerClassnames = classnames(styles.header, headerClassName);
  const accordionItemContentClassName = classnames(
    styles.content,
    {
      [styles.contentOpen]: isAccordionOpen,
      [styles.transition]: isInit,
    },
    contentClassName,
  );

  return (
    <div {...restProps} data-testid="accordion" className={rootClassnames}>
      <div
        className={headerClassnames}
        data-testid="accordionHeader"
        role="button"
        aria-expanded={isAccordionOpen}
        aria-controls={sectionId}
        id={labelId}
        tabIndex={-1}
        ref={headerRef}
        onClick={onHandleToggle}
        onKeyDown={onHandleKeyDown}
      >
        {title}
        {!hiddenArrowIcon && (
          <ArrowDown
            className={styles.arrow}
            tabIndex={0}
            aria-label="arrow_down"
          />
        )}
      </div>
      <div
        className={accordionItemContentClassName}
        role="region"
        aria-labelledby={labelId}
        id={sectionId}
        style={{ height: isAccordionOpen ? height : 0 }}
        data-testid="accordionContent"
      >
        <div className={classnames(styles.body, bodyClassName)} ref={bodyRef}>
          {children}
        </div>
      </div>
    </div>
  );
};

AccordionItem.displayName = 'AccordionItem';
